import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ErrorHandler, useErrorHandler } from "../utils/errorHandler";
import authService from "../services/authService";
import vercelAnalytics from "../services/vercelAnalyticsService";
import "./Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [abortController, setAbortController] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { error, handleError, clearError } = useErrorHandler();

  // Efeito para animação de entrada e capturar mensagens
  useEffect(() => {
    setIsVisible(true);

    // Verificar se o modo escuro está ativado
    const darkModeEnabled = document.body.classList.contains('dark-mode');
    setIsDarkMode(darkModeEnabled);

    // Capturar mensagem do state (redirecionamentos de rotas protegidas)
    if (location.state?.message) {
      setInfoMessage(location.state.message);
      // Limpar o state para não mostrar a mensagem novamente
      window.history.replaceState({}, document.title);
    }

    // Verificar se já está logado apenas se há um token válido
    const token = authService.getToken();
    if (token && ErrorHandler.isTokenValid(token)) {
      // Só redireciona se o token for válido estruturalmente
      navigate("/dashboard", { replace: true });
         } else if (token) {
       // Se há token mas é inválido, limpar silenciosamente
       localStorage.removeItem('token');
       localStorage.removeItem('user');
     }
  }, [navigate, location.state]);

  // Efeito de segurança para evitar loading infinito
  useEffect(() => {
    if (loading) {
      if (process.env.NODE_ENV === 'development') {
        console.log("⏰ Loading iniciado, configurando timeout de segurança...");
      }
      const safetyTimeout = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.warn("⚠️ Timeout de segurança ativado - resetando loading");
        }
        setLoading(false);
        handleError(new Error("A requisição demorou muito para responder. Tente novamente."), 'Login');
      }, 30000); // 30 segundos

      return () => {
        if (process.env.NODE_ENV === 'development') {
          console.log("🧹 Limpando timeout de segurança");
        }
        clearTimeout(safetyTimeout);
      };
    }
  }, [loading, handleError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (process.env.NODE_ENV === 'development') {
      console.log("🔄 Iniciando processo de login...");
    }
    
    // Criar um novo AbortController para esta requisição
    const controller = new AbortController();
    setAbortController(controller);
    setLoading(true);
    clearError();
    setInfoMessage("");

    try {
      // Validações básicas
      if (!email.trim()) {
        throw new Error("Por favor, informe seu e-mail.");
      }
      if (!password.trim()) {
        throw new Error("Por favor, informe sua senha.");
      }

      // Promessa principal - login com timeout de 15 segundos
      const loginPromise = (async () => {
        if (process.env.NODE_ENV === 'development') {
          console.log("✉️ Fazendo login com:", email);
        }
        
        const response = await authService.login({
          email: email.trim(),
          password: password.trim()
        });

        if (controller.signal.aborted) {
          throw new Error("Login cancelado pelo usuário");
        }

        return response;
      })();

      // Promessa de timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Timeout: A requisição demorou muito para responder")), 15000);
      });

      // Aguardar a primeira promessa que for resolvida
      const result = await Promise.race([loginPromise, timeoutPromise]);

      if (process.env.NODE_ENV === 'development') {
        console.log("✅ Login bem-sucedido:", result.user);
      }

      // Armazenar dados do usuário
      localStorage.setItem('token', result.token);
      localStorage.setItem('userEmail', email);
      
      // Track successful login
      vercelAnalytics.trackLogin('email');
      vercelAnalytics.trackMainFunnel.discovery('login_page');
      
      window.location.href = '/dashboard';

    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error("❌ Erro no login:", err);
      }

      handleError(err, 'Login');
    } finally {
      if (process.env.NODE_ENV === 'development') {
        console.log("🏁 Finalizando processo de login - setLoading(false)");
      }
      setLoading(false);
      setAbortController(null);
    }
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.body.classList.toggle("dark-mode", !isDarkMode);
  };

  const handleCancel = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log("🛑 Cancelando login...");
    }
    if (abortController) {
      abortController.abort();
    }
    setLoading(false);
    setAbortController(null);
  };

  return (
    <div className={`login-page ${isDarkMode ? "dark" : ""} ${isVisible ? "visible" : ""}`}>
      <div className="login-header">
        <Link to="/" className="logo">
          <span className="logo-icon">❤️</span>
          <span className="logo-text">Casal Resolve</span>
        </Link>
        <button onClick={toggleDarkMode} className="theme-toggle">
          {isDarkMode ? "☀️" : "🌙"}
        </button>
      </div>

      <div className="login-container">
        <div className="login-box">
          <div className="login-content">
            <h1 className="login-title">Bem-vindo de volta!</h1>
            <p className="login-subtitle">Faça login para acessar sua conta e continuar fortalecendo seu relacionamento.</p>

            {/* Mensagem informativa (redirecionamentos) */}
            {infoMessage && (
              <div className="info-message">
                <span className="info-icon">ℹ️</span>
                {infoMessage}
              </div>
            )}

            {/* Mensagem de erro */}
            {error && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="email">E-mail</label>
                <div className="input-wrapper">
                  <span className="input-icon">✉️</span>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Digite seu e-mail"
                    disabled={loading}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="password">Senha</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Digite sua senha"
                    disabled={loading}
                    required
                  />
                </div>
              </div>
              <div className="forgot-password">
                <Link to="/forgot-password">Esqueceu sua senha?</Link>
              </div>
              <div className="button-group">
                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="login-loading">
                      <div className="pulse-dots">
                        <div className="pulse-dot"></div>
                        <div className="pulse-dot"></div>
                        <div className="pulse-dot"></div>
                      </div>
                      <span>Processando...</span>
                    </span>
                  ) : (
                    "Entrar"
                  )}
                </button>
                
                {loading && (
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
            <div className="register-link">
              <p>Ainda não tem conta?</p>
              <Link to="/register" className="register-button">Cadastre-se</Link>
            </div>
          </div>

          <div className="login-decoration">
            <div className="decoration-content">
              <h2>Resolva conflitos e fortaleça seu relacionamento</h2>
              <p>O Casal Resolve oferece ferramentas para melhorar a comunicação e resolver conflitos de forma saudável.</p>
              <div className="decoration-image"></div>
            </div>
          </div>
        </div>
      </div>

      <footer className="login-footer">
        <p>&copy; 2025 Casal Resolve - Plataforma para resolução de conflitos</p>
      </footer>
    </div>
  );
};

export default Login;
